import type { FormFieldDef } from '../types';

export type VisibilityConditions = NonNullable<FormFieldDef['conditions']>;

type FieldCondition = VisibilityConditions['conditions'][number];

// Single source of truth for form-engine visibility. Both the UI
// (useGetShowCondition) and validation (parsing/validations.ts) decide
// visibility through this module, so a field the user cannot see can never be
// validated. Two parallel implementations are what produced PS-6221 (MFA=NO)
// and PS-6343 (backup frequency=Never).

// Deduped so a value can be looked up by fieldId rather than by position.
// Array.from — Set spread ([...new Set()]) is broken under this project's
// Jest/Babel target.
export const getDependentFieldIds = (
  visibilityConditions: VisibilityConditions
): string[] =>
  Array.from(
    new Set(
      visibilityConditions.conditions.map((condition) => condition.fieldId)
    )
  );

export const areVisibilityConditionsMet = (
  visibilityConditions: VisibilityConditions,
  valueByFieldId: Record<string, any>
): boolean => {
  const results = visibilityConditions.conditions.map((condition) =>
    evaluateFieldCondition(condition, valueByFieldId[condition.fieldId])
  );

  if (visibilityConditions.operator === 'OR') {
    return results.some((result) => result);
  }

  // operator === 'AND'
  return results.every((result) => result);
};

const evaluateFieldCondition = (
  condition: FieldCondition,
  value: any
): boolean => {
  // When the dependency is unavailable: EQUALS/CONTAINS -> hide;
  // NOT_EQUALS -> show (undefined !== X is true).
  if (value == null) {
    return condition.operator === 'NOT_EQUALS';
  }

  switch (condition.operator) {
    case 'EQUALS':
      return value.value === condition.value;
    case 'NOT_EQUALS':
      return value.value !== condition.value;
    case 'CONTAINS':
      return (
        value.values?.some(
          (item: { value: string | number }) => item.value === condition.value
        ) ?? false
      );
    default:
      return false;
  }
};
